import { useEffect, useMemo, useState } from "react";
import { PARANOIDI_UPDATES } from "../data/paranoidiUpdates";
import { PARANOIDI_TERMINAL_MESSAGES } from "../data/paranoidiTerminalMessages";

const COPY = {
  fi: {
    sectionEyebrow: "Paranoidi / päivitykset",
    sectionTitle: "Mitä uutta?",
    historyOpen: "Näytä päivityshistoria",
    historyClose: "Piilota päivityshistoria",
    historyTitle: "Päivityshistoria",
    liveMeta: "LIVE NYT",
    statusMeta: "PARANOIDI",
    liveGeneric: "● LIVE NYT — Paranoidia käytetään juuri nyt.",
    liveCount: (count) =>
      `● LIVE NYT — ${count} esiintyjää aktiivisena Paranoidissa.`,
  },
  en: {
    sectionEyebrow: "Paranoidi / updates",
    sectionTitle: "What's new?",
    historyOpen: "View update history",
    historyClose: "Hide update history",
    historyTitle: "Update history",
    liveMeta: "LIVE NOW",
    statusMeta: "PARANOIDI",
    liveGeneric: "● LIVE NOW — Paranoidi is being used right now.",
    liveCount: (count) =>
      `● LIVE NOW — ${count} performers active on Paranoidi.`,
  },
};

function formatUpdateDate(value) {
  const [year, month, day] = String(value || "").split("-");

  if (!year || !month || !day) {
    return value || "";
  }

  return `${day}.${month}.${year}`;
}

function getApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined" ? window.PARANOIDI_API_BASE_URL : "";

  if (fromWindow) {
    return String(fromWindow).replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:4000/api";
  }

  return "/backend/api";
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
}

export default function UpdatesTerminal({ language = "fi" }) {
  const normalizedLanguage = language === "en" ? "en" : "fi";
  const copy = COPY[normalizedLanguage];
  const prefersReducedMotion = usePrefersReducedMotion();

  // Varsinainen päivityshistoria pysyy erillään terminalin status- ja LIVE-riveistä.
  const updates = useMemo(
    () =>
      PARANOIDI_UPDATES.map((update) => ({
        ...update,
        type: "update",
        text: update[normalizedLanguage],
      })),
    [normalizedLanguage]
  );

  const statusMessages = useMemo(
    () =>
      PARANOIDI_TERMINAL_MESSAGES.map((message) => ({
        ...message,
        type: "status",
        text: message[normalizedLanguage],
      })),
    [normalizedLanguage]
  );

  const [liveActivity, setLiveActivity] = useState({
    hasActivePerformers: false,
    activePerformers: null,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleText, setVisibleText] = useState("");
  const [phase, setPhase] = useState("typing");
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    async function fetchLiveActivity() {
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/public/homepage-activity`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            cache: "no-store",
          }
        );

        const data = await response.json().catch(() => null);

        if (cancelled) {
          return;
        }

        if (!response.ok || !data?.ok) {
          setLiveActivity({
            hasActivePerformers: false,
            activePerformers: null,
          });
          return;
        }

        setLiveActivity({
          hasActivePerformers: data.hasActivePerformers === true,
          activePerformers: Number.isInteger(data.activePerformers)
            ? data.activePerformers
            : null,
        });
      } catch {
        if (!cancelled) {
          // LIVE-data on lisämauste. Etusivun terminali jatkaa normaalisti,
          // vaikka backend olisi hetkellisesti tavoittamattomissa.
          setLiveActivity({
            hasActivePerformers: false,
            activePerformers: null,
          });
        }
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        fetchLiveActivity();
      }
    }

    fetchLiveActivity();

    intervalId = window.setInterval(() => {
      fetchLiveActivity();
    }, 60 * 1000);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;

      if (intervalId) {
        window.clearInterval(intervalId);
      }

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);

  const liveItem = useMemo(() => {
    if (!liveActivity.hasActivePerformers) {
      return null;
    }

    const count = liveActivity.activePerformers;

    return {
      id: "live-now",
      type: "live",
      text:
        Number.isInteger(count) && count >= 3
          ? copy.liveCount(count)
          : copy.liveGeneric,
    };
  }, [copy, liveActivity]);

  // Terminalissa vuorottelevat oikeat tuotepäivitykset ja lyhyet statusviestit.
  // LIVE-rivi lisätään aikaisin kiertoon, jos backend kertoo aktiivisesta käytöstä.
  const terminalItems = useMemo(() => {
    const items = [];
    const maxLength = Math.max(updates.length, statusMessages.length);

    for (let index = 0; index < maxLength; index += 1) {
      if (updates[index]) {
        items.push(updates[index]);
      }

      if (statusMessages[index]) {
        items.push(statusMessages[index]);
      }

      if (index === 0 && liveItem) {
        items.push(liveItem);
      }
    }

    return items;
  }, [liveItem, statusMessages, updates]);

  const activeItem = terminalItems[activeIndex] || terminalItems[0];
  const activeText = activeItem?.text || "";

  const activeMeta = useMemo(() => {
    if (!activeItem) {
      return "";
    }

    if (activeItem.type === "live") {
      return copy.liveMeta;
    }

    if (activeItem.type === "status") {
      return copy.statusMeta;
    }

    return formatUpdateDate(activeItem.date);
  }, [activeItem, copy]);

  useEffect(() => {
    setActiveIndex(0);
    setVisibleText("");
    setPhase("typing");
  }, [normalizedLanguage]);

  useEffect(() => {
    if (activeIndex < terminalItems.length) {
      return;
    }

    setActiveIndex(0);
    setVisibleText("");
    setPhase("typing");
  }, [activeIndex, terminalItems.length]);

  useEffect(() => {
    if (!activeItem) {
      return undefined;
    }

    if (prefersReducedMotion) {
      setVisibleText(activeText);
      setPhase("hold");
      return undefined;
    }

    let timeoutId;

    if (phase === "typing") {
      if (visibleText.length < activeText.length) {
        timeoutId = window.setTimeout(() => {
          setVisibleText(activeText.slice(0, visibleText.length + 1));
        }, 42);
      } else {
        timeoutId = window.setTimeout(() => {
          setPhase("hold");
        }, 300);
      }
    } else if (phase === "hold") {
      timeoutId = window.setTimeout(() => {
        setPhase("deleting");
      }, 4800);
    } else if (phase === "deleting") {
      if (visibleText.length > 0) {
        timeoutId = window.setTimeout(() => {
          setVisibleText((current) => current.slice(0, -1));
        }, 22);
      } else {
        setPhase("idle");
      }
    } else if (phase === "idle") {
      timeoutId = window.setTimeout(() => {
        setActiveIndex(
          (current) => (current + 1) % terminalItems.length
        );
        setPhase("typing");
      }, 2200);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    activeItem,
    activeText,
    phase,
    prefersReducedMotion,
    terminalItems.length,
    visibleText,
  ]);

  const indicatorWindow = useMemo(() => {
    const maxIndicators = 8;
    const maxStart = Math.max(terminalItems.length - maxIndicators, 0);
    const start = Math.min(
      Math.max(activeIndex - Math.floor(maxIndicators / 2), 0),
      maxStart
    );

    return terminalItems
      .slice(start, start + maxIndicators)
      .map((item, offset) => ({
        item,
        itemIndex: start + offset,
      }));
  }, [activeIndex, terminalItems]);

  if (!activeItem) {
    return null;
  }

  return (
    <section className="relative overflow-hidden border-b border-white/10 bg-[#050505] px-5 py-12 sm:px-8 lg:px-10 lg:py-14">
      <style>
        {`
          @keyframes paranoidiTerminalCursor {
            0%, 46% { opacity: 1; }
            47%, 100% { opacity: 0; }
          }

          .paranoidi-terminal-cursor {
            animation: paranoidiTerminalCursor 820ms steps(1, end) infinite;
          }

          @media (prefers-reduced-motion: reduce) {
            .paranoidi-terminal-cursor {
              animation: none !important;
            }
          }
        `}
      </style>

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_50%,rgba(16,185,129,0.08),transparent_24rem),radial-gradient(circle_at_82%_50%,rgba(239,68,68,0.08),transparent_26rem)]" />

      <div className="relative mx-auto max-w-7xl">
        <div className="overflow-hidden rounded-[2rem] border border-emerald-400/20 bg-[#050807] shadow-2xl shadow-black/35">
          <div className="flex flex-col gap-4 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.75)]" />

              <p className="truncate text-[0.68rem] font-black uppercase tracking-[0.22em] text-zinc-300">
                {copy.sectionEyebrow}
              </p>
            </div>

            <p
              className={[
                "font-mono text-[0.68rem] font-bold tracking-[0.12em]",
                activeItem.type === "live"
                  ? "text-emerald-400"
                  : "text-zinc-600",
              ].join(" ")}
            >
              {activeMeta}
            </p>
          </div>

          <div className="px-5 py-6 sm:px-6 sm:py-7 lg:px-8">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-red-400">
              {copy.sectionTitle}
            </p>

            <div className="mt-4 min-h-[4.75rem] rounded-2xl border border-white/10 bg-black/55 px-4 py-4 font-mono text-[0.95rem] font-bold leading-7 text-zinc-100 shadow-inner shadow-black/40 sm:min-h-[4rem] sm:px-5 sm:text-base">
              <span aria-hidden="true" className="text-emerald-400">
                &gt;&nbsp;
              </span>

              <span aria-hidden="true">{visibleText}</span>

              <span
                aria-hidden="true"
                className="paranoidi-terminal-cursor ml-0.5 inline-block text-red-400"
              >
                █
              </span>

              <span className="sr-only">{activeText}</span>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {indicatorWindow.map(({ item, itemIndex }) => (
                  <span
                    key={item.id}
                    className={[
                      "h-1.5 rounded-full transition-all duration-300",
                      itemIndex === activeIndex
                        ? "w-6 bg-red-400"
                        : "w-1.5 bg-zinc-700",
                    ].join(" ")}
                    aria-hidden="true"
                  />
                ))}
              </div>

              <button
                type="button"
                aria-expanded={historyOpen}
                aria-controls="paranoidi-update-history"
                onClick={() => setHistoryOpen((current) => !current)}
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.035] px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-zinc-200 transition hover:-translate-y-0.5 hover:border-red-400/30 hover:bg-red-500/[0.07] hover:text-white"
              >
                {historyOpen ? copy.historyClose : copy.historyOpen}
                <span className="ml-2" aria-hidden="true">
                  {historyOpen ? "↑" : "↓"}
                </span>
              </button>
            </div>
          </div>

          {historyOpen ? (
            <div
              id="paranoidi-update-history"
              className="border-t border-white/10 bg-black/35 px-5 py-6 sm:px-6 lg:px-8"
            >
              <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">
                {copy.historyTitle}
              </h3>

              <div className="mt-5 grid gap-3">
                {updates.map((update) => (
                  <article
                    key={update.id}
                    className="grid gap-2 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-4 sm:grid-cols-[7.5rem_1fr] sm:items-start sm:gap-5"
                  >
                    <time
                      dateTime={update.date}
                      className="font-mono text-[0.72rem] font-black tracking-[0.08em] text-zinc-500"
                    >
                      {formatUpdateDate(update.date)}
                    </time>

                    <p className="text-sm font-semibold leading-6 text-zinc-300">
                      {update.text}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
